import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useForm } from "react-hook-form";
import { toast } from "react-hot-toast";
import {
  Avatar,
  Button,
  IconButton,
  TextField,
  Box,
  Typography,
} from "@mui/material";
import {
  ExpandLess as ChevronUpIcon,
  ExpandMore as ChevronDownIcon,
} from "@mui/icons-material";
import "./TaskInput.scss";

const TaskInput = ({ taskInput, onSave }) => {
  const [isBodyVisible, setIsBodyVisible] = useState(true);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      serAmount: taskInput?.serAmount || 0,
      serSupply: taskInput?.serSupply || 0,
      serStation: taskInput?.serStation || "",
      serNote: taskInput?.serNote || "",
    },
  });

  useEffect(() => {
    reset({
      serAmount: taskInput?.serAmount || 0,
      serSupply: taskInput?.serSupply || 0,
      serStation: taskInput?.serStation || "",
      serNote: taskInput?.serNote || "",
    });
  }, [taskInput, reset]);

  const onSubmit = (data) => {
    if (!taskInput) {
      toast.error("Please select a task first");
      return;
    }

    if (!taskInput.serTitle) {
      toast.error("Can't update value for empty task");
      return;
    }

    const updatedTask = {
      ...taskInput,
      ...data,
    };

    onSave(updatedTask);
  };

  const toggleBodyVisibility = () => {
    setIsBodyVisible(!isBodyVisible);
  };

  if (!taskInput) {
    return <div>Select a task to view details</div>;
  }

  const { avatar, name, serTitle, column } = taskInput;

  return (
    <Box className="task-input-container">
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box className="task-input-header" display="flex" alignItems="center">
          {avatar && (
            <Avatar src={avatar} alt={name} className="task-input-avatar" />
          )}
          <Typography variant="h6" className="task-input-title">
            {name} / {serTitle} - {column?.toUpperCase()}
          </Typography>
          <Box display="flex" ml="auto">
            <IconButton onClick={toggleBodyVisibility}>
              {isBodyVisible ? <ChevronUpIcon /> : <ChevronDownIcon />}
            </IconButton>
            <Button variant="contained" color="primary" type="submit">
              Save
            </Button>
          </Box>
        </Box>
        {isBodyVisible && (
          <Box className="task-input-body" mt={2}>
            <Box className="task-input-row">
              <TextField
                label="Amount"
                type="number"
                variant="outlined"
                fullWidth
                size="small"
                error={!!errors.serAmount}
                helperText={errors.serAmount ? "Amount is required" : ""}
                {...register("serAmount", {
                  valueAsNumber: true,
                  required: true,
                })}
                InputLabelProps={{ shrink: true }} // Keep label always on top
              />
            </Box>
            <Box className="task-input-row">
              <TextField
                label="Supply"
                type="number"
                variant="outlined"
                fullWidth
                size="small"
                error={!!errors.serSupply}
                helperText={errors.serSupply ? "Supply is required" : ""}
                {...register("serSupply", {
                  setValueAs: (v) => (v === "" ? undefined : parseFloat(v)), // Ensure decimal values are accepted
                  required: true,
                })}
                inputProps={{ step: "any", min: 0 }} // Allow any decimal value
                InputLabelProps={{ shrink: true }} // Keep label always on top
              />
            </Box>
            <Box className="task-input-row" hidden>
              <TextField
                label="Station"
                type="text"
                variant="outlined"
                fullWidth
                size="small"
                error={!!errors.serStation}
                helperText={errors.serStation ? "Station is required" : ""}
                {...register("serStation", { required: false })}
                InputLabelProps={{ shrink: true }} // Keep label always on top
              />
            </Box>

            <Box className="task-input-row">
              <TextField
                label="Note"
                variant="outlined"
                fullWidth
                size="small"
                multiline
                rows={2}
                {...register("serNote")}
                InputLabelProps={{ shrink: true }} // Keep label always on top
              />
            </Box>
          </Box>
        )}
      </form>
    </Box>
  );
};

TaskInput.propTypes = {
  taskInput: PropTypes.shape({
    serAmount: PropTypes.number,
    serSupply: PropTypes.number,
    serStation: PropTypes.string,
    serNote: PropTypes.string,
    avatar: PropTypes.string,
    name: PropTypes.string,
    serTitle: PropTypes.string,
    column: PropTypes.string,
  }),
  onSave: PropTypes.func.isRequired,
};

export default TaskInput;
